import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShoppingCart, Wallet, Banknote } from 'lucide-react';
import api from '@/lib/api';
import { money } from '@/lib/helpers';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';

const PAY_METHODS = ['CASH', 'UPI', 'CARD', 'BANK'];

// The same "QUICK ADD" menu used on the Business Dashboard — Purchase, Expense,
// and Record payment — made reusable so it can sit on the Home dashboard too.
export function QuickAddMenu({ align = 'end', className = '' }) {
  const navigate = useNavigate();
  const [quickPay, setQuickPay] = useState(false);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="quick-add-trigger" aria-label="Quick add"
            className={`flex h-10 items-center gap-2 rounded-xl border border-primary/60 bg-primary/10 px-3 text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${className}`}>
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Quick Add</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-56">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">Quick add</DropdownMenuLabel>
          <DropdownMenuItem data-testid="qa-purchase" onClick={() => navigate('/purchase')}>
            <ShoppingCart className="mr-2 h-4 w-4 text-primary" />Purchase
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="qa-expense" onClick={() => navigate('/expenses')}>
            <Wallet className="mr-2 h-4 w-4 text-amber-500" />Expense
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="qa-payment" onClick={() => setQuickPay(true)}>
            <Banknote className="mr-2 h-4 w-4 text-primary" />Record payment
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {quickPay && <QuickPaymentDialog onClose={() => setQuickPay(false)} />}
    </>
  );
}

function QuickPaymentDialog({ onClose }) {
  const qc = useQueryClient();
  const [sid, setSid] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CASH');
  const [busy, setBusy] = useState(false);
  const { data: suppliers = [] } = useQuery({
    queryKey: ['qp-suppliers'],
    queryFn: async () => (await api.get('/suppliers')).data.data,
  });
  const withDue = suppliers.filter((s) => Number(s.outstanding || 0) > 0);
  const selected = suppliers.find((s) => s.id === sid);
  const due = Number(selected?.outstanding || 0);

  const save = async () => {
    if (!sid) return toast.error('Choose a supplier');
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error('Enter a valid amount');
    setBusy(true);
    try {
      await api.post(`/suppliers/${sid}/payment`, { amount: amt, method });
      toast.success('Payment recorded');
      qc.invalidateQueries({ queryKey: ['qp-suppliers'] });
      qc.invalidateQueries({ queryKey: ['home-dashboard'] });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record payment'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-payment-dialog">
        <DialogHeader>
          <DialogTitle>Quick payment</DialogTitle>
          <DialogDescription>Settle a supplier balance without leaving the dashboard.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Supplier</Label>
            <Select value={sid} onValueChange={setSid}>
              <SelectTrigger data-testid="qp-supplier" className="mt-1"><SelectValue placeholder={withDue.length ? 'Select supplier with balance' : 'No suppliers with a balance'} /></SelectTrigger>
              <SelectContent>
                {(withDue.length ? withDue : suppliers).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}{Number(s.outstanding || 0) > 0 ? ` · ${money(s.outstanding)} due` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selected && <p className="text-xs text-muted-foreground">Outstanding: <span className="font-mono text-destructive">{money(due)}</span></p>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input data-testid="qp-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="mt-1" />
              {due > 0 && <button type="button" data-testid="qp-fill-full" onClick={() => setAmount(String(due))} className="mt-1 rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-white/5">Pay full</button>}
            </div>
            <div>
              <Label className="text-xs">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="qp-method" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="qp-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="qp-save">{busy ? 'Saving…' : 'Record payment'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
